/**
 * Bundle format v2 — the contract between the pipeline's `bundle` stage and
 * every consumer (the viewer, the headset harness, the client).
 *
 * A bundle is a `manifest.json` plus chunk files. A chunk holds, for each part
 * it carries at one level of detail: positions (Float32 × 3 × v), normals
 * (Int16 × 3 × v, normalised on the GPU, as the source viewer does) and
 * indices (Uint32 × i). Each section starts on a 4-byte boundary. The manifest
 * records the byte offsets per part per level, so a consumer slices typed
 * arrays straight out of the chunk buffer; nothing is parsed.
 */

export const FORMAT_VERSION = 2 as const;

export type Side = "left" | "right" | "median" | "unknown";

export interface LevelRange {
  /** Index into `Manifest.chunks`. */
  chunk: number;
  /** Byte offsets inside the chunk buffer. */
  pos: number;
  nrm: number;
  idx: number;
  /** Vertex and index counts. */
  v: number;
  i: number;
  /** Triangle count (i / 3), kept explicit for reports. */
  tris: number;
  /** Geometric error of this level in metres (0 for the finest shipped level of a part). */
  error: number;
}

export interface PartRecord {
  id: string;
  name: string;
  concept: string;
  system: string;
  side: Side;
  mirror: string | null;
  bounds: [number[], number[]];
  centre: number[];
  /** Keyed by level id (`hero`, `near`, `far`, …). */
  levels: Record<string, LevelRange>;
  /** True when a pipeline stage quarantined this part and shipped an earlier stage's output. */
  fallback?: { stage: string; reason: string };
}

export interface SystemRecord {
  id: string;
  name: string;
  colour: string;
  /** Peel and draw-order layer: 0 outermost. */
  layer: number;
  defaultVisible: boolean;
  material: "opaque" | "translucent" | "doubleSided";
}

export interface LevelSpec {
  id: string;
  /** Target triangle ratio used by the pipeline (1 = the repaired mesh). */
  ratio: number;
  /** Relative error bound used by the pipeline. */
  error: number;
}

export interface ChunkRecord {
  url: string;
  gz?: string;
  bytes: number;
  level: string;
  systems: string[];
}

export interface ConceptRecord {
  id: string;
  name: string;
  parts: string[];
}

export interface Manifest {
  version: typeof FORMAT_VERSION;
  id: string;
  name: string;
  licence: { spdx: string; holder: string; url: string };
  bounds: [number[], number[]];
  systems: SystemRecord[];
  levels: LevelSpec[];
  parts: PartRecord[];
  concepts: ConceptRecord[];
  chunks: ChunkRecord[];
  provenance: {
    run: string;
    recipe: string;
    generated: string;
    stages: { name: string; version: string }[];
    fallbacks: number;
  };
}

export interface PartArrays {
  positions: Float32Array;
  normals: Int16Array;
  indices: Uint32Array;
}

/** Slice one part's arrays out of a chunk buffer. Zero copies. */
export function partArrays(buffer: ArrayBuffer, range: LevelRange): PartArrays {
  return {
    positions: new Float32Array(buffer, range.pos, range.v * 3),
    normals: new Int16Array(buffer, range.nrm, range.v * 3),
    indices: new Uint32Array(buffer, range.idx, range.i),
  };
}

/** Round a byte offset up to the next 4-byte boundary. */
export function align4(n: number): number {
  return (n + 3) & ~3;
}

/** Quantise a unit normal component to the Int16 range the format stores. */
export function quantiseNormal(n: number): number {
  const v = Math.round(Math.max(-1, Math.min(1, n)) * 32767);
  return v;
}

export function isManifest(value: unknown): value is Manifest {
  if (!value || typeof value !== "object") return false;
  const m = value as Partial<Manifest>;
  return (
    m.version === FORMAT_VERSION &&
    typeof m.id === "string" &&
    Array.isArray(m.parts) &&
    Array.isArray(m.chunks) &&
    Array.isArray(m.systems) &&
    Array.isArray(m.levels)
  );
}
